import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { makeHoverPrefetch, prefetchDossier } from '@/components/deal/dossier/prefetchDossier'
import {
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  Coins,
  Columns3,
  Filter,
  Layers,
  MapPin,
  RefreshCw,
  Target,
  TrendingUp,
  Upload,
  UserRoundX,
  X,
  ArrowUpDown,
} from 'lucide-react'
import clsx from 'clsx'
import { usePipelineLoans } from '@/hooks/useDemoDataAccess'
import { useLoansWithLiveTriggers } from '@/hooks/useLiveTriggerAlerts'
import { COMMAND_CENTER_LOANS } from '@/data/commandCenterLoans'
import ExportIcons from '@/components/shared/ExportIcons'
import { DemoEmptyState } from '@/components/shared/DemoEmptyState'
import DataReviewPanel from '@/components/review/DataReviewPanel'
import { loansService } from '@/services/loans'
import { ReviewGapProvider } from './pipeline-intelligence/ReviewGapMarker'
import { pipelineViewsService } from '@/services/pipelineViews'
import { pipelineExportSettingsService } from '@/services/pipelineExportSettings'
import {
  BACKSHOP_TEMPLATE,
  DEFAULT_PIPELINE_EXPORT_SETTINGS,
} from '@/services/pipelineExportDefaults'
import { useGridDensity } from '@/hooks/useGridDensity'
import { useDealDossierStore } from '@/store/dealDossierStore'
import { DealGrid } from './pipeline-intelligence/DealGrid'
import { GridColumnPanel } from './pipeline-intelligence/GridColumnPanel'
import { RecencySelect } from './pipeline-intelligence/RecencySelect'
import { layoutColumnIds, screenLayoutFor, withStageOrder, type StageOrder } from './pipeline-intelligence/screenLayout'
import { DealInspector } from './pipeline-intelligence/DealInspector'
import { DealWorkspaceModal } from './pipeline-intelligence/DealWorkspaceModal'
import { FieldExportButton } from './pipeline-intelligence/FieldExportButton'
import DealBookImportDialog from './pipeline-intelligence/DealBookImportDialog'
import { useCanImportDealBook } from './pipeline-intelligence/useCanImportDealBook'
import PipelineReconciliation from './pipeline-intelligence/PipelineReconciliation'
import { StageRail } from './pipeline-intelligence/StageRail'
import { ScopeBanner } from './pipeline-intelligence/deadScope'
import { markDemoFilename, markDemoTitle } from '@/config/demoExportMark'
import { buildViewGrid } from './pipeline-intelligence/reportExport'
import {
  DEFAULT_VIEW_SORT,
  OLDEST_FIRST_SORT,
  exportSavedViewXlsx,
  filterViewLoans,
  sortViewLoans,
  type ViewSort,
} from './pipeline-intelligence/savedViewExport'
import {
  applyLoanFilters,
  completeness,
  criticalMissing,
  dealScopeOf,
  needsAddress,
  distinctSecondaryUnderwriters,
  fundedAmount,
  inKillReasonScope,
  killReasonOf,
  loanExceptions,
  recencyLabel,
  requestedCommitment,
  slaBreached,
  distinctAnalysts,
  NO_ANALYST,
} from './pipeline-intelligence/helpers'
import { loanKillReason } from './pipeline-intelligence/loanFigures'
import { formatTrackerMoney } from './pipeline-intelligence/stageTracker'
import { usePipelineFocus } from './pipeline-intelligence/pipelineFocus'
import { formatMoneyByCurrency, sumMoneyByCurrency } from './pipeline-intelligence/currency'
import { applyReportFilters } from './pipeline-intelligence/reporting'
import type {
  ColumnId,
  DealScope,
  EditableLoanPatch,
  FilterState,
  InspectorTab,
  MetricFilter,
  SavedView,
} from './pipeline-intelligence/types'
import { DEFAULT_COLUMNS, EMPTY_FILTERS, STAGES } from './pipeline-intelligence/types'

function Metric({
  label,
  value,
  sub,
  icon: Icon,
  active = false,
  goldValue = false,
  disabled = false,
  onClick,
}: {
  label: string
  value: string
  sub: string
  icon: typeof TrendingUp
  active?: boolean
  goldValue?: boolean
  /** A figure that does not apply to the rows on screen: shown, not clickable. */
  disabled?: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={clsx(
        'flex items-center gap-2 rounded-md border px-2.5 py-1.5 text-left transition',
        disabled && 'cursor-default opacity-60',
        active
          ? 'border-amber-300/40 bg-amber-300/[0.07]'
          : clsx('border-white/[0.08] bg-[#0c0c0c]', !disabled && 'hover:border-amber-300/25'),
      )}
    >
      <Icon className="h-3.5 w-3.5 flex-shrink-0 text-[#FFD86B]" />
      <span className="min-w-0">
        <span className="block text-[9px] font-medium uppercase tracking-[0.14em] text-[#FFD86B]/85">{label}</span>
        <span
          className={clsx(
            'mt-0.5 block text-[15px] font-semibold leading-none',
            goldValue ? 'text-[#FFD86B]' : 'text-white',
          )}
        >
          {value}
        </span>
        <span className="mt-0.5 block truncate text-[9px] leading-tight text-stone-600">{sub}</span>
      </span>
    </button>
  )
}

/** The Deal status filter's choices, in `FilterState.dealStatus` terms. A killed
 *  deal and a screened-out one are both `deal_status = dead` on the server — the
 *  kill reason says which — so one choice shows both. */
const DEAL_STATUS_OPTIONS: ReadonlyArray<readonly [DealScope, string]> = [
  ['active', 'Active pipeline'],
  ['dead', 'Killed & screened out'],
  ['all', 'Active + killed & screened out'],
]

function FilterPanel({
  filters,
  setFilters,
  stages,
  assetClasses,
  assignees,
  secondaryUnderwriters,
  analysts = [],
  years,
  onClose,
}: {
  filters: FilterState
  setFilters: (value: FilterState | ((current: FilterState) => FilterState)) => void
  stages: string[]
  assetClasses: string[]
  assignees: string[]
  secondaryUnderwriters: string[]
  analysts?: string[]
  years: string[]
  onClose: () => void
}) {
  return (
    <div className="absolute right-0 top-8 z-40 w-80 rounded-lg border border-amber-300/15 bg-[#111] p-3 shadow-xl">
      <div className="mb-3 flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-[0.14em] text-stone-500">Query filters</div>
        <button type="button" onClick={onClose} className="text-stone-500 hover:text-stone-200">
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
      {(
        [
          ['stage', 'Stage', stages],
          ['assetClass', 'Asset class', assetClasses],
          ['assignee', 'Assignee', assignees],
          // Gate 0 F1: the closers' underwriter. Its own filter, beside the
          // Responsible Party's - the two are different people on a deal.
          ['secondaryUnderwriter', 'Secondary underwriter', secondaryUnderwriters],
          // T-57: every deal is supposed to have an analyst; the deals with none
          // are one choice, so a desk can see who has no help.
          ['analyst', 'Analyst', [NO_ANALYST, ...analysts]],
          ['year', 'Year', years],
        ] as const
      ).map(([key, label, options]) => (
        <label key={key} className="mb-2 block">
          <span className="text-[9px] uppercase tracking-[0.14em] text-stone-600">{label}</span>
          <select
            value={filters[key] ?? 'All'}
            onChange={(e) => setFilters((current) => ({ ...current, [key]: e.target.value }))}
            className="mt-1 w-full rounded border border-stone-700 bg-black/40 px-2 py-1.5 text-[12px] text-stone-100 [color-scheme:dark]"
          >
            <option value="All" className="bg-black text-white">
              All
            </option>
            {options.map((option) => (
              <option key={option} value={option} className="bg-black text-white">
                {option}
              </option>
            ))}
          </select>
        </label>
      ))}
      {/* T-10 (2 Nov run-through): killed and screened-out deals were recorded but
          only the 24 Sep reset views could show any dead deal, so a deal the desk
          killed or screened out for a business reason never appeared on the
          Pipeline. This reads the same `dealStatus` scope a saved view stores;
          the live pipeline stays the default. */}
      <label className="mb-2 block">
        <span className="text-[9px] uppercase tracking-[0.14em] text-stone-600">Deal status</span>
        <select
          aria-label="Deal status"
          data-testid="deal-status-filter"
          value={dealScopeOf(filters)}
          onChange={(e) => {
            const next = e.target.value as DealScope
            setFilters((current) => ({
              ...current,
              dealStatus: next === 'active' ? undefined : next,
              // A pick here reads every killed deal, not only a view's one kill reason.
              killReason: undefined,
            }))
          }}
          className="mt-1 w-full rounded border border-stone-700 bg-black/40 px-2 py-1.5 text-[12px] text-stone-100 [color-scheme:dark]"
        >
          {DEAL_STATUS_OPTIONS.map(([value, label]) => (
            <option key={value} value={value} className="bg-black text-white">
              {label}
            </option>
          ))}
        </select>
        {dealScopeOf(filters) !== 'active' && killReasonOf(filters) ? (
          <span className="mt-1 flex items-center gap-1 text-[10px] text-stone-500">
            <span>
              Only deals killed for:{' '}
              {loanKillReason({
                killedAt: null,
                deadReason: null,
                killReasonCode: killReasonOf(filters),
                killedAtStage: null,
              })}
            </span>
            <button
              type="button"
              data-testid="deal-status-every-reason"
              onClick={() => setFilters((current) => ({ ...current, killReason: undefined }))}
              className="underline hover:text-stone-300"
            >
              Show every reason
            </button>
          </span>
        ) : null}
      </label>
      {/* R-12: the deal that was just added or changed. */}
      <label className="mb-2 block">
        <span className="text-[9px] uppercase tracking-[0.14em] text-stone-600">Added or modified</span>
        <RecencySelect
          value={filters.recency}
          onChange={(recency) => setFilters((current) => ({ ...current, recency }))}
          className="mt-1 w-full rounded border border-stone-700 bg-black/40 px-2 py-1.5 text-[12px] text-stone-100 [color-scheme:dark]"
        />
      </label>
      <button
        type="button"
        // The scope belongs to the view, not to the filter bar: clearing the
        // filters on a dead-deal view must not quietly turn it into the live one,
        // or the reset's dead deals into every dead deal.
        onClick={() =>
          setFilters((current) => ({
            ...EMPTY_FILTERS,
            ...(current.dealStatus ? { dealStatus: current.dealStatus } : {}),
            ...(current.killReason ? { killReason: current.killReason } : {}),
          }))
        }
        className="mt-1 w-full rounded border border-stone-700 px-2 py-1.5 text-[11px] text-stone-300"
      >
        Clear filters
      </button>
    </div>
  )
}


/** The filters a chip names when they are set (T-62), with the label the desk reads. */
const ACTIVE_FILTER_CHIPS = [
  ['stage', 'Stage'],
  ['assetClass', 'Asset class'],
  ['assignee', 'Responsible Party'],
  ['secondaryUnderwriter', 'Secondary underwriter'],
  ['analyst', 'Analyst'],
  ['year', 'Year'],
] as const satisfies ReadonlyArray<readonly [keyof FilterState, string]>

const STAGE_ORDER_KEY = 'cmos_pipeline_stage_order'

/** This viewer's choice, remembered on this browser; Funded first (Backshop's order) by default. */
function readStageOrder(): StageOrder {
  try {
    return localStorage.getItem(STAGE_ORDER_KEY) === 'triage-first' ? 'triage-first' : 'funded-first'
  } catch {
    return 'funded-first'
  }
}

function writeStageOrder(order: StageOrder): void {
  try {
    localStorage.setItem(STAGE_ORDER_KEY, order)
  } catch {
    /* storage unavailable: the choice lasts for this page only */
  }
}

export default function PipelineIntelligence() {
  const queryClient = useQueryClient()
  // The book places deals past Triage, an admin act; the API enforces it too.
  const canImportBook = useCanImportDealBook()
  const [bookImportOpen, setBookImportOpen] = useState(false)
  const [filters, setFilters] = useState<FilterState>(EMPTY_FILTERS)
  // Which deals the selected view reads: the live pipeline (the default), the
  // dead deals kept for comparison, or both.
  const scope = dealScopeOf(filters)
  const {
    loans: bookLoansRaw,
    activeLoans,
    deadLoans,
    isLoading,
    isLive,
    deadLoading,
    deadError,
    needsLiveData,
    refetchDead,
  } = usePipelineLoans('pipeline', COMMAND_CENTER_LOANS, scope)
  // GET /loans/ carries no triggers; the Exceptions rule and the Inspector read
  // them from loan.triggers, so the live book gets its /triggers/ rows here.
  const loans = useLoansWithLiveTriggers(bookLoansRaw, isLive)
  // Warm the dossier for the row under the pointer, so the double-click that
  // follows opens a populated dossier rather than a shell that fills in.
  const hoverPrefetch = useMemo(
    () => makeHoverPrefetch((loan) => prefetchDossier(queryClient, loan, { demo: !isLive })),
    [queryClient, isLive],
  )
  const viewsQuery = useQuery({
    queryKey: ['pipeline-views'],
    queryFn: () => pipelineViewsService.list(),
    staleTime: 30_000,
  })

  const savedViews = viewsQuery.data ?? []
  const [activeViewId, setActiveViewId] = useState<string>('active-pipeline')

  const exportSettingsQuery = useQuery({
    queryKey: ['pipeline-export-settings'],
    queryFn: () => pipelineExportSettingsService.get(),
    staleTime: 60_000,
  })
  const exportSettings = exportSettingsQuery.data ?? DEFAULT_PIPELINE_EXPORT_SETTINGS
  // Backshop is the layout unless the Reporting page chose Legacy: Print, PDF and
  // Excel then export the one fixed template, whatever columns the screen shows.
  const backshopLayout = exportSettings.layout !== 'legacy'
  // The report's definition, as the server serves it. The grid is drawn from this and from
  // nothing typed here, so the page cannot drift from what Print prints.
  const reportLayout = backshopLayout ? exportSettings.backshopLayout : undefined

  // Keep the view switcher and exports working even when the saved-view
  // service is down: fall back to a client-only draft.
  const localFallbackView = useMemo<SavedView>(
    () => ({
      id: 'local-workspace',
      name: 'Pipeline Report',
      description: 'Saved-view service unavailable — designs apply to this session only.',
      status: 'Draft',
      ownerEmail: 'local',
      version: 0,
      updatedAt: null,
      // The report's own columns when the server has told us them: even a draft matches Print.
      columns: reportLayout ? layoutColumnIds(reportLayout) : DEFAULT_COLUMNS,
      filters: EMPTY_FILTERS,
      versions: [],
      isLocal: true,
    }),
    [reportLayout],
  )
  // Never null. An errored service and an empty list both left the switcher
  // dead before; worse, `isLoading` flips on every React Query retry, so gating
  // the grid on it discarded the working column/filter selection mid-session.
  const activeView =
    savedViews.find((view) => view.id === activeViewId) ?? savedViews[0] ?? localFallbackView

  // Why each blank critical cell is blank. A demo account has no intake behind
  // it, so there is nothing to explain and nothing to ask for.
  const reviewGapsQuery = useQuery({
    queryKey: ['pipeline-review-gaps'],
    queryFn: () => loansService.getReviewGaps(),
    enabled: isLive,
    // A pipeline user without `pipeline:view` on intake data gets a 403, which
    // is an answer, not a transient failure.
    retry: false,
    staleTime: 60_000,
  })

  const [visibleColumns, setVisibleColumns] = useState<ColumnId[]>(DEFAULT_COLUMNS)
  // Columns added on screen after the report layout's own (R-12: Last modified,
  // Date added). Print, PDF and Excel still export the report's columns alone.
  const [screenExtras, setScreenExtras] = useState<ColumnId[]>([])
  const [selectedId, setSelectedId] = useState<string | null>(null)
  // A deal another screen asked to open (the Deal Locator's "Open in
  // Pipeline"). It stays inspectable even when this view's filters hide it.
  const [focusedId, setFocusedId] = useState<string | null>(null)
  const [inspectorTab, setInspectorTab] = useState<InspectorTab>('overview')
  const [filterPanelOpen, setFilterPanelOpen] = useState(false)
  const [columnPanelOpen, setColumnPanelOpen] = useState(false)
  const [viewMenuOpen, setViewMenuOpen] = useState(false)
  // Register B1 — remembered per user, so it follows them to another machine.
  const { density, setDensity } = useGridDensity()
  // The order the desk chose by clicking a header; until then the grid opens in its own order.
  const [chosenSort, setChosenSort] = useState<ViewSort | null>(null)
  // The grid shows the report's layout — Backshop's columns and headers, deals under their stage —
  // when the view's columns ARE the report's. A saved view with columns of its own is not the report.
  const screenLayout = useMemo(
    () => screenLayoutFor(reportLayout, visibleColumns),
    [reportLayout, visibleColumns],
  )
  const [stageOrder, setStageOrderState] = useState<StageOrder>(readStageOrder)
  const setStageOrder = (next: StageOrder) => {
    setStageOrderState(next)
    writeStageOrder(next)
  }
  const orderedScreenLayout = useMemo(() => withStageOrder(screenLayout, stageOrder), [screenLayout, stageOrder])
  // Over the report layout each stage reads oldest to newest by Days in Status, as Print does; any
  // other view opens largest request first.
  const sort: ViewSort = chosenSort ?? (screenLayout ? OLDEST_FIRST_SORT : DEFAULT_VIEW_SORT)
  const [toast, setToast] = useState<string | null>(null)
  const [inspectorOpen, setInspectorOpen] = useState(true)
  // The Data Intelligence Review, opened from a blank or unsure cell on the
  // deal and the field the marker named.
  const [reviewTarget, setReviewTarget] = useState<{ loanId: string; field: string } | null>(null)
  const openReview = useCallback((loanId: string, field: string) => setReviewTarget({ loanId, field }), [])
  const [workspaceId, setWorkspaceId] = useState<string | null>(null)
  const dossierWorkspaceId = useDealDossierStore((s) => s.workspaceLoanId)
  const closeDossierWorkspace = useDealDossierStore((s) => s.closeWorkspace)
  const effectiveWorkspaceId = workspaceId ?? dossierWorkspaceId

  useEffect(() => {
    if (dossierWorkspaceId) {
      setSelectedId(dossierWorkspaceId)
      setWorkspaceId(dossierWorkspaceId)
    }
  }, [dossierWorkspaceId])

  // The draft shown while the saved views are unavailable takes the report's columns the moment the
  // server has told us them, so even a draft matches Print.
  const draftColumns = activeView?.isLocal ? activeView.columns : null
  useEffect(() => {
    if (!activeView) return
    setFilters(activeView.filters ?? EMPTY_FILTERS)
    setVisibleColumns(activeView.columns?.length ? activeView.columns : DEFAULT_COLUMNS)
  }, [activeView?.id, draftColumns]) // eslint-disable-line react-hooks/exhaustive-deps -- hydrate on view switch

  useEffect(() => {
    if (savedViews.length && !savedViews.some((view) => view.id === activeViewId)) {
      setActiveViewId(savedViews[0].id)
    }
  }, [savedViews, activeViewId])

  useEffect(() => {
    if (!selectedId && loans[0]) setSelectedId(loans[0].id)
  }, [loans, selectedId])

  useEffect(() => {
    if (!toast) return
    const timer = window.setTimeout(() => setToast(null), 2800)
    return () => window.clearTimeout(timer)
  }, [toast])

  const assetClasses = useMemo(() => Array.from(new Set(loans.map((loan) => loan.type))).sort(), [loans])
  const assignees = useMemo(
    () => Array.from(new Set(loans.map((loan) => loan.assignee).filter(Boolean))).sort(),
    [loans],
  )
  const secondaryUnderwriters = useMemo(() => distinctSecondaryUnderwriters(loans), [loans])
  const analysts = useMemo(() => distinctAnalysts(loans), [loans])
  const years = useMemo(
    () =>
      Array.from(new Set(loans.map((loan) => (loan.originated || '').slice(0, 4)).filter(Boolean)))
        .sort()
        .reverse(),
    [loans],
  )

  // The dead deals this view reads: all of them, or the ones killed for the reason it names.
  const killReason = killReasonOf(filters)
  const killReasonText = killReason
    ? loanKillReason({ killedAt: null, deadReason: null, killReasonCode: killReason, killedAtStage: null })
    : null
  const deadInScope = useMemo(
    () => deadLoans.filter((loan) => inKillReasonScope(loan, filters)),
    [deadLoans, filters],
  )

  // The figures and the stage rail count ONE population: the live deals for an
  // active view and for the combined one (dead deals never enter a live total),
  // the dead deals for a dead-deal view. The grid below may show both.
  const tileScope: DealScope = scope === 'dead' ? 'dead' : 'active'
  const bookLoans = useMemo(
    () =>
      applyReportFilters(
        applyLoanFilters(loans, { ...filters, stage: 'All', metric: 'all', dealStatus: tileScope }),
        filters.report,
      ),
    [loans, filters, tileScope],
  )

  // The same filter and sort Reporting's Run Now applies to a saved view, so
  // the toolbar and Run Now render one document (savedViewExport.ts).
  const filteredLoans = useMemo(() => filterViewLoans(loans, filters), [loans, filters])

  const sortedLoans = useMemo(() => sortViewLoans(filteredLoans, sort), [filteredLoans, sort])

  usePipelineFocus(loans, !isLoading, ({ id, found }) => {
    if (!found) {
      setToast(`Deal ${id} is not on the Pipeline — dead and archived deals stay in the Deal Locator.`)
      return
    }
    setSelectedId(id)
    setFocusedId(id)
    setInspectorOpen(true)
    if (!filteredLoans.some((loan) => loan.id === id)) {
      setToast(`Deal ${id} is outside this view's filters — it is open in the Inspector.`)
    }
  })

  const selected =
    sortedLoans.find((loan) => loan.id === selectedId) ??
    (selectedId && selectedId === focusedId ? loans.find((loan) => loan.id === selectedId) : undefined) ??
    sortedLoans[0] ??
    null

  // What the grid draws: the view's columns, and over the report layout any
  // column added on screen after the report's own.
  const gridColumns = useMemo(
    () =>
      screenLayout
        ? [...visibleColumns, ...screenExtras.filter((id) => !visibleColumns.includes(id))]
        : visibleColumns,
    [screenLayout, visibleColumns, screenExtras],
  )

  // One basis for every deal: the total commitment (OVR-19).
  const requested = bookLoans.reduce((sum, loan) => sum + (requestedCommitment(loan) ?? 0), 0)
  // Deals hold their money in their own currency and nothing converts it, so a
  // book in more than one currency has no single total: it reads per currency.
  const requestedByCurrency = sumMoneyByCurrency(bookLoans, requestedCommitment)
  const fullyFunded = bookLoans.reduce((sum, loan) => sum + (fundedAmount(loan) ?? 0), 0)
  const missingCount = bookLoans.filter((loan) => criticalMissing(loan).length > 0).length
  const needsAddressCount = bookLoans.filter((loan) => needsAddress(loan)).length
  const exceptionCount = bookLoans.filter((loan) => loanExceptions(loan).length > 0).length
  // Register F1/F2 — the assignment clock's alert surface. The server has sent
  // this on every loan read for a while and nothing counted it, so a deal
  // sitting overdue with its owner never reached anyone who could move it.
  const slaBreachCount = bookLoans.filter((loan) => slaBreached(loan)).length

  const snapshotId = useMemo(() => {
    const iso = new Date().toISOString()
    const stamp = `${iso.slice(0, 10).split('-').join('')}${iso.slice(11, 19).split(':').join('')}`
    return `snap-${activeView?.id ?? 'pipeline'}-v${activeView?.version ?? 0}-${stamp}`
  }, [activeView?.id, activeView?.version, filters, visibleColumns, filteredLoans.length])

  // Every grid filter the server can apply, so the fixed client layout covers
  // the same deals the grid shows (the report reads the view's vocabulary).
  const exportFilters = {
    stage: filters.stage !== 'All' ? filters.stage : undefined,
    asset_type: filters.assetClass !== 'All' ? filters.assetClass : undefined,
    assignee: filters.assignee !== 'All' ? filters.assignee : undefined,
    // The server's reports read this too (`builder._pipeline_loans`), so a field
    // export or a server render of the view narrows to the deals the grid shows.
    secondary_underwriter:
      filters.secondaryUnderwriter && filters.secondaryUnderwriter !== 'All'
        ? filters.secondaryUnderwriter
        : undefined,
    analyst: filters.analyst && filters.analyst !== 'All' ? filters.analyst : undefined,
    year: filters.year !== 'All' ? filters.year : undefined,
    search: filters.search.trim() || undefined,
    // `builder.within_recency` applies it, so a print of a filtered screen is that screen.
    recency: filters.recency || undefined,
    metric: filters.metric === 'funded' ? filters.metric : undefined,
    // The server's reports read the scope too, so a field export or a server
    // render of a dead-deal view carries the dead deals — and an active view is
    // untouched (the keys are absent).
    dealStatus: scope !== 'active' ? scope : undefined,
    include_dead: scope !== 'active' ? true : undefined,
    // ...and the dead deals it narrows to, so the server render and the grid read the same ones.
    killReason: scope !== 'active' && killReason ? killReason : undefined,
    snapshotId,
    viewId: activeView?.id,
    viewName: activeView?.name,
    viewVersion: activeView?.version,
    columns: visibleColumns,
    // The open report's row filters (column filters, date range), so a server
    // export built from the view narrows to the rows the grid shows. Only the
    // filters: the design (sort, grouping, totals, style) stays the Studio's.
    reportRowFilters: filters.report
      ? {
          columnFilters: filters.report.columnFilters,
          dateFrom: filters.report.dateFrom,
          dateTo: filters.report.dateTo,
        }
      : undefined,
    query: filters,
  }

  // G3 — persist a grid column reorder as the user's saved default. The new order
  // is written straight to the active view's working columns (not a new immutable
  // version), so the default survives reload without polluting the version history
  // that scheduled reports read from. Debounced so a burst of moves is one write.
  const reorderTimer = useRef<number | null>(null)
  const persistColumnOrder = useMutation({
    mutationFn: ({ viewId, columns }: { viewId: string; columns: ColumnId[] }) =>
      pipelineViewsService.update(viewId, { columns }),
    onSuccess: async (view) => {
      await queryClient.invalidateQueries({ queryKey: ['pipeline-views'] })
      setToast(`Column order saved to ${view.name}`)
    },
    onError: () => setToast('Column order kept for this session — the saved default was not updated'),
  })

  const handleReorder = (columns: ColumnId[]) => {
    setVisibleColumns(columns)
    const view = activeView
    // Only persist to a real saved view (not the local fallback), and only when
    // the order actually differs from what is stored on that view.
    const isPersisted = savedViews.some((v) => v.id === view.id)
    const changed =
      columns.length !== (view.columns?.length ?? 0) ||
      columns.some((id, i) => id !== view.columns?.[i])
    if (!isPersisted || !changed) return
    if (reorderTimer.current) window.clearTimeout(reorderTimer.current)
    reorderTimer.current = window.setTimeout(() => {
      persistColumnOrder.mutate({ viewId: view.id, columns })
    }, 700)
  }

  useEffect(() => () => {
    if (reorderTimer.current) window.clearTimeout(reorderTimer.current)
  }, [])

  const editMutation = useMutation({
    // `version` is the row's updatedAt as loaded (CMP-04): a save from a stale
    // row is refused with 409 instead of overwriting a newer edit.
    mutationFn: ({ id, patch, version }: { id: string; patch: EditableLoanPatch; version?: string | null }) =>
      loansService.update(id, patch, version ?? null),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['loans'] })
      setToast('Deal updated')
    },
  })

  const setMetric = (metric: MetricFilter) => {
    setFilters((current) => ({
      ...current,
      metric,
      stage: metric === 'funded' ? 'Funded' : metric === 'all' ? 'All' : current.stage === 'Funded' ? 'All' : current.stage,
    }))
  }

  if (!isLive && activeLoans.length === 0) {
    return (
      <DemoEmptyState
        moduleId="pipeline"
        title="Pipeline Intelligence"
        description="Demo data is off. Ingest or create live deals to populate the connected pipeline workspace."
      />
    )
  }

  return (
    <div className="flex h-[calc(100vh-4rem)] flex-col bg-[#080808] text-stone-200">
      <div className="border-b border-amber-300/10 px-3 py-1.5">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex min-w-0 items-baseline gap-2">
            <div className="text-[9px] uppercase tracking-[0.16em] text-amber-200/70">Pipeline</div>
            <div className="truncate text-[13px] font-medium text-stone-100">Origination workspace</div>
          </div>
          <div className="flex flex-wrap items-center gap-1.5">
            <div className="relative flex items-center gap-2">
              <span className="whitespace-nowrap text-[10px] font-medium text-stone-400">
                Saved View:
              </span>
              <button
                type="button"
                onClick={() => setViewMenuOpen((open) => !open)}
                className="inline-flex items-center gap-1.5 rounded border border-amber-300/20 bg-amber-300/[0.06] px-2 py-1 text-[10px] text-amber-100"
              >
                {viewsQuery.isLoading ? 'Loading views…' : activeView.name}
                <span className="font-mono text-stone-500">
                  {activeView.isLocal ? 'unsaved' : `v${activeView.version}`}
                </span>
                <ChevronDown className="h-3 w-3" />
              </button>
              {viewMenuOpen ? (
                <div className="absolute right-0 top-10 z-30 w-72 rounded-lg border border-amber-300/15 bg-[#111] p-2 shadow-xl">
                  {viewsQuery.isLoading ? (
                    <div className="px-2 py-3 text-[12px] text-stone-500">Loading saved views…</div>
                  ) : savedViews.length === 0 ? (
                    <div className="px-2 py-3 text-[12px] text-amber-200/80">
                      Saved views unavailable. Report Studio opens on an unsaved local draft —
                      versioning, scheduling, and sharing stay disabled until the service responds.
                    </div>
                  ) : (
                    savedViews.map((view) => (
                      <button
                        key={view.id}
                        type="button"
                        onClick={() => {
                          setActiveViewId(view.id)
                          setViewMenuOpen(false)
                        }}
                        className={clsx(
                          'block w-full rounded px-2 py-2 text-left hover:bg-white/[0.03]',
                          view.id === activeView?.id && 'bg-amber-300/[0.07]',
                        )}
                      >
                        <div className="text-[12px] text-stone-100">{view.name}</div>
                        <div className="text-[10px] text-stone-500">
                          v{view.version} · {view.status} · {view.ownerEmail}
                          {dealScopeOf(view.filters) !== 'active' ? (
                            <span className="text-red-300">
                              {' '}· {dealScopeOf(view.filters) === 'dead' ? 'Dead deals' : 'Active + dead deals'}
                            </span>
                          ) : null}
                        </div>
                      </button>
                    ))
                  )}
                </div>
              ) : null}
            </div>
            {backshopLayout ? (
              // One server template for every format: Print prints its PDF and
              // Excel downloads its workbook, so the two cannot differ. The rows
              // are the deals this view's filters select (the server reads the
              // same vocabulary), including the metric chip.
              <ExportIcons
                template={BACKSHOP_TEMPLATE}
                formats={['pdf', 'xlsx']}
                filters={{
                  ...exportFilters,
                  metric: filters.metric !== 'all' ? filters.metric : undefined,
                }}
                filenameBase={`${(activeView?.name ?? 'pipeline').replace(/\s+/g, '_')}_v${activeView?.version ?? 0}_${snapshotId}`}
                printTitle="Print the Pipeline Report (Backshop layout)"
              />
            ) : (
              // The demo book leaves marked (title, notice, filename): a file made from the
              // illustrative sample must not read as the desk's own pipeline.
              <ExportIcons
                template={exportSettings.template}
                formats={exportSettings.formats}
                filters={exportFilters}
                filenameBase={markDemoFilename(
                  `${(activeView?.name ?? 'pipeline').replace(/\s+/g, '_')}_v${activeView?.version ?? 0}_${snapshotId}`,
                  !isLive,
                )}
                getViewGrid={
                  exportSettings.useOnScreenColumns
                    ? () => buildViewGrid(sortedLoans, visibleColumns, markDemoTitle(activeView?.name ?? 'Pipeline Report', !isLive))
                    : undefined
                }
                exportXlsxLocal={
                  exportSettings.useOnScreenColumns
                    ? () =>
                        exportSavedViewXlsx(
                          { name: activeView?.name },
                          sortedLoans,
                          `${(activeView?.name ?? 'pipeline').replace(/\s+/g, '_')}_v${activeView?.version ?? 0}_${snapshotId}`,
                          sort,
                          visibleColumns,
                          { demo: !isLive },
                        )
                    : undefined
                }
              />
            )}
            <FieldExportButton
              filters={exportFilters}
              filenameBase={`${(activeView?.name ?? 'pipeline').replace(/\s+/g, '_')}_v${activeView?.version ?? 0}`}
            />
            {canImportBook && isLive ? (
              <button
                type="button"
                onClick={() => setBookImportOpen(true)}
                className="inline-flex items-center gap-1 rounded border border-amber-300/20 px-2 py-1 text-[10px] text-amber-100 hover:bg-amber-300/[0.06]"
                title="Create deals from a Backshop Status / Pipeline workbook"
              >
                <Upload className="h-3 w-3" /> Import deal book
              </button>
            ) : null}
            <div className="inline-flex items-center gap-1 rounded border border-white/[0.06] px-1.5 py-0.5 text-[9px] text-stone-500">
              <RefreshCw className="h-2.5 w-2.5" />
              {isLive ? 'Live' : 'Demo'}
              {isLoading ? ' · loading' : ` · ${loans.length}`}
            </div>
          </div>
        </div>
      </div>

      <DealBookImportDialog
        open={bookImportOpen}
        onClose={() => setBookImportOpen(false)}
        onCommitted={() => queryClient.invalidateQueries({ queryKey: ['loans'] })}
      />
      <StageRail
        loans={bookLoans}
        activeStage={filters.stage}
        mode={scope === 'dead' ? 'dead' : 'live'}
        onSelectStage={(stage) => setFilters((current) => ({ ...current, stage, metric: 'all' }))}
      />

      <div className="grid gap-2 border-b border-amber-300/10 px-4 py-2 md:grid-cols-3 lg:grid-cols-6">
        <Metric
          label={scope === 'dead' ? 'Dead Deals' : 'Total Deals'}
          value={String(bookLoans.length)}
          sub={
            scope === 'dead'
              ? 'Killed — not in live totals'
              : scope === 'all'
                ? 'Active only — dead excluded'
                : 'Across all stages'
          }
          icon={Layers}
          active={filters.metric === 'all' && filters.stage === 'All'}
          onClick={() => setMetric('all')}
        />
        <Metric
          label="Requested"
          value={
            requestedByCurrency.totals.length === 0
              ? formatTrackerMoney(requested)
              : formatMoneyByCurrency(requestedByCurrency, formatTrackerMoney)
          }
          sub={requestedByCurrency.mixed ? 'Per currency — never added across' : 'Sum of total commitments'}
          icon={Coins}
          goldValue
          active={false}
          onClick={() => setMetric('all')}
        />
        <Metric
          label="Funded (Closed)"
          value={formatTrackerMoney(fullyFunded)}
          sub="Disbursed on closed loans"
          icon={CheckCircle2}
          active={filters.metric === 'funded'}
          onClick={() => setMetric('funded')}
        />
        <Metric
          label="Missing Critical"
          value={String(missingCount)}
          sub="Fields requiring entry"
          icon={AlertTriangle}
          active={filters.metric === 'missing'}
          onClick={() => setMetric('missing')}
        />
        <Metric
          label="Needs address"
          value={String(needsAddressCount)}
          sub="Missing street, city or state"
          icon={MapPin}
          active={filters.metric === 'needsAddress'}
          onClick={() => setMetric('needsAddress')}
        />
        <Metric
          label="Exceptions"
          value={String(exceptionCount)}
          sub="Credit, aging, or triggers"
          icon={Target}
          active={filters.metric === 'exceptions'}
          onClick={() => setMetric('exceptions')}
        />
        <Metric
          label="Owner Overdue"
          // The assignment clock stops when a deal is killed, so a count over
          // dead deals would read 0 — a figure where there is no measurement.
          value={scope === 'dead' ? '—' : String(slaBreachCount)}
          sub={scope === 'dead' ? 'Clock stops when a deal is killed' : 'Past assignment SLA'}
          icon={UserRoundX}
          disabled={scope === 'dead'}
          active={scope !== 'dead' && filters.metric === 'sla'}
          onClick={() => setMetric('sla')}
        />
      </div>

      <ScopeBanner
        scope={scope}
        deadCount={deadInScope.length}
        reason={killReasonText}
        activeCount={activeLoans.length}
        shown={filteredLoans.length}
        loading={deadLoading}
        error={deadError}
        needsLiveData={needsLiveData}
        onRetry={() => void refetchDead()}
      />

      {/* Register P1: the deal-list count, reconciled against intake, stated
          where it is read rather than left to be inferred from another screen. */}
      {/* `filteredLoans`, not `bookLoans`: the grid renders the former, and
          `bookLoans` deliberately ignores the stage and metric filters so the
          tiles above can count across the whole book. Passing the book here
          made the line read "5 live pipeline rows" while the grid showed four,
          with nothing saying why — the exact disagreement P1 exists to end. */}
      {/* The row count and the grid's controls share one row, with the controls
          on the right. They used to sit on their own row below, at the far
          LEFT — and both panels open `right-0`, so a 320px panel anchored to a
          button 60px from the content edge extended a quarter of its width
          under the left navigation, where it could not be reached. Right-
          aligned, the same anchor opens the panel back INTO the page. */}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-amber-300/10 px-4 py-1">
        <div className="min-w-0 flex-1">
          {/* The reconciliation counts LIVE pipeline rows against intake; over
              dead deals it would say "Showing N of M live rows" about rows that
              are not live, so it speaks only for an active view. */}
          <PipelineReconciliation onScreen={filteredLoans.length} live={isLive && scope === 'active'} bare />
        </div>

        <div className="ml-auto flex shrink-0 flex-wrap items-center justify-end gap-1.5">
        {/* T-62 (2 Nov 2026): filtered to one person, nothing on the screen said the
            list was theirs. Every active filter is named where the grid is read,
            with one click to take it off. */}
        {ACTIVE_FILTER_CHIPS.map(([key, label]) => {
          const value = filters[key]
          if (!value || value === 'All') return null
          return (
            <button
              key={key}
              type="button"
              data-testid={`filter-chip-${key}`}
              onClick={() => setFilters((current) => ({ ...current, [key]: 'All' }))}
              title={`Show every ${label.toLowerCase()}`}
              className="inline-flex items-center gap-1 rounded border border-amber-300/30 bg-amber-300/[0.08] px-2 py-1 text-[11px] text-amber-100"
            >
              <span className="text-amber-200/70">{label}:</span> {value} <X className="h-3 w-3" aria-hidden />
            </button>
          )
        })}
        {screenLayout ? (
          // T-63 (2 Nov 2026): the stage groups read Funded first, as Backshop prints them;
          // an originator works the book from Triage up. A choice per viewer, on screen only:
          // Print, PDF and Excel keep the report's order.
          <button
            type="button"
            data-testid="stage-order-toggle"
            onClick={() => setStageOrder(stageOrder === 'triage-first' ? 'funded-first' : 'triage-first')}
            title="Flip the order of the stage groups on this screen. Print, PDF and Excel keep the report's order."
            className="inline-flex items-center gap-1 rounded border border-stone-700 px-2 py-1 text-[10px] text-stone-300"
          >
            <ArrowUpDown className="h-3 w-3" aria-hidden />
            {stageOrder === 'triage-first' ? 'Triage first' : 'Funded first'}
          </button>
        ) : null}
        {/* A recency filter narrows the grid to a handful of deals; said where
            the grid is read, with one click to take it off. */}
        {filters.recency ? (
          <button
            type="button"
            data-testid="recency-chip"
            onClick={() => setFilters((current) => ({ ...current, recency: undefined }))}
            title="Show deals added or modified at any time"
            className="inline-flex items-center gap-1 rounded border border-amber-300/30 bg-amber-300/[0.08] px-2 py-1 text-[10px] text-amber-100"
          >
            {recencyLabel(filters.recency)} <X className="h-3 w-3" aria-hidden />
          </button>
        ) : null}
        <div className="relative">
          <button
            type="button"
            onClick={() => {
              setFilterPanelOpen((open) => !open)
              setColumnPanelOpen(false)
            }}
            className="inline-flex items-center gap-1 rounded border border-stone-700 px-2 py-1 text-[10px] text-stone-300"
          >
            <Filter className="h-3 w-3" /> Filters
          </button>
          {filterPanelOpen ? (
            <FilterPanel
              filters={filters}
              setFilters={setFilters}
              stages={STAGES}
              assetClasses={assetClasses}
              assignees={assignees}
              secondaryUnderwriters={secondaryUnderwriters}
              analysts={analysts}
              years={years}
              onClose={() => setFilterPanelOpen(false)}
            />
          ) : null}
        </div>

        {screenLayout ? (
          // Print, PDF and Excel draw the report's columns whatever the screen holds, so the report's
          // columns cannot be changed here. A column can be added after them, on screen only (R-12).
          <span
            data-testid="report-layout-chip"
            title="These are the columns of the Backshop pipeline report, in its order — the same layout Print, PDF and Excel export. Columns added with Columns show on this screen only. Choose another saved view to pick your own columns."
            className="inline-flex items-center gap-1 rounded border border-amber-300/20 bg-amber-300/[0.06] px-2 py-1 text-[10px] text-amber-100"
          >
            <Columns3 className="h-3 w-3" /> Report layout
            {gridColumns.length > visibleColumns.length ? (
              <span className="text-amber-200/70">+ {gridColumns.length - visibleColumns.length} on screen</span>
            ) : null}
          </span>
        ) : null}
        <div className="relative">
          <button
            type="button"
            onClick={() => {
              setColumnPanelOpen((open) => !open)
              setFilterPanelOpen(false)
            }}
            className="inline-flex items-center gap-1 rounded border border-stone-700 px-2 py-1 text-[10px] text-stone-300"
          >
            <Columns3 className="h-3 w-3" /> Columns
          </button>
          {columnPanelOpen ? (
            screenLayout ? (
              <GridColumnPanel
                visible={gridColumns}
                fixed={visibleColumns}
                setVisible={(next) => setScreenExtras(next.filter((id) => !visibleColumns.includes(id)))}
                onClose={() => setColumnPanelOpen(false)}
              />
            ) : (
              <GridColumnPanel
                visible={visibleColumns}
                setVisible={setVisibleColumns}
                onClose={() => setColumnPanelOpen(false)}
              />
            )
          ) : null}
        </div>

        {/* Register B1: density is a choice, not a single larger size imposed on
            everyone. Compact is the default, so no existing view changes. */}
        <div className="inline-flex rounded border border-stone-700 p-0.5" role="group" aria-label="Grid density">
          {([
            ['compact', 'Dense'],
            ['comfortable', 'Roomy'],
          ] as const).map(([mode, label]) => (
            <button
              key={mode}
              type="button"
              onClick={() => setDensity(mode)}
              aria-pressed={density === mode}
              title={
                mode === 'compact'
                  ? 'More deals per screen'
                  : 'Larger type — easier to tell 0 from 8'
              }
              className={`rounded px-2 py-1 text-[10px] ${
                density === mode ? 'bg-amber-400/15 text-amber-200' : 'text-stone-400 hover:text-stone-200'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
        </div>
      </div>

      {/* The Inspector reads the review gaps too: a contested sponsor names
          nobody there, as in the grid, rather than its borrowing entity. */}
      <ReviewGapProvider gaps={reviewGapsQuery.data?.gaps} onOpen={openReview}>
      <div className="flex min-h-0 flex-1">
        <div className="flex min-w-0 flex-1 flex-col">
            <DealGrid
              editableResponsibleParty={isLive}
              density={density}
              layout={orderedScreenLayout}
              loans={sortedLoans}
              columns={gridColumns}
              selectedId={selected?.id ?? null}
              onSelect={(loan) => {
                setSelectedId(loan.id)
                setInspectorOpen(true)
                // A selected row is the one most likely to be opened next.
                prefetchDossier(queryClient, loan, { demo: !isLive })
              }}
              onOpen={(loan) => {
                setSelectedId(loan.id)
                useDealDossierStore.getState().openDossier(loan.id, 'overview', loan)
              }}
              onHover={hoverPrefetch.enter}
              onHoverEnd={hoverPrefetch.leave}
              sort={sort}
              onSort={(id) =>
                setChosenSort(
                  sort.id === id
                    ? { id, direction: sort.direction === 'asc' ? 'desc' : 'asc' }
                    : { id, direction: 'desc' },
                )
              }
              // The report's columns are fixed: a drag that reordered them would stop the screen
              // matching what Print prints, so there is none while the report layout is shown.
              onReorder={screenLayout ? undefined : handleReorder}
            />
        </div>

        {inspectorOpen && selected ? (
          <DealInspector
            loan={selected}
            allLoans={loans}
            tab={inspectorTab}
            setTab={setInspectorTab}
            onClose={() => setInspectorOpen(false)}
            saving={editMutation.isPending}
            onSave={async (patch) => {
              await editMutation.mutateAsync({ id: selected.id, patch, version: selected.updatedAt ?? selected.createdAt })
            }}
          />
        ) : null}
      </div>
      </ReviewGapProvider>

      {effectiveWorkspaceId ? (() => {
        const workspaceLoan = loans.find((candidate) => candidate.id === effectiveWorkspaceId)
        // The deal can disappear underneath the modal — a refetch, a filter, a
        // kill. Close rather than render an empty shell.
        if (!workspaceLoan) return null
        return (
          <DealWorkspaceModal
            loan={workspaceLoan}
            allLoans={loans}
            saving={editMutation.isPending}
            onClose={() => {
              setWorkspaceId(null)
              closeDossierWorkspace()
            }}
            onSave={async (patch) => {
              await editMutation.mutateAsync({ id: workspaceLoan.id, patch, version: workspaceLoan.updatedAt ?? workspaceLoan.createdAt })
            }}
          />
        )
      })() : null}

      {reviewTarget ? (
        <DataReviewPanel
          loanId={reviewTarget.loanId}
          initialField={reviewTarget.field}
          onClose={() => setReviewTarget(null)}
        />
      ) : null}

      {toast ? (
        <div className="fixed bottom-4 right-4 z-50 rounded-md border border-amber-300/20 bg-[#111] px-3 py-2 text-[12px] text-amber-100 shadow-xl">
          {toast}
        </div>
      ) : null}

      {/* Keep completeness helper referenced for metric context in empty states */}
      {filteredLoans.length === 0 && loans.length > 0 ? (
        <div className="sr-only">{completeness(loans[0])}</div>
      ) : null}
    </div>
  )
}
